import type { CSSProperties } from 'react'
import StarterKit from '@tiptap/starter-kit'
import { TextStyle } from '@tiptap/extension-text-style'
import FontFamily from '@tiptap/extension-font-family'
import Color from '@tiptap/extension-color'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import type { AnyExtension, EditorOptions } from '@tiptap/core'
import { FontSize } from '@/components/sequences/extensions/FontSize'
import { PersonalizationToken } from '@/components/sequences/extensions/PersonalizationToken'
import {
  ImageUpload,
  type ImageImporter,
  type ImageMeasurer,
  type ImageUploader,
} from '@/components/sequences/extensions/ImageUpload'

/**
 * Config condivisa degli editor email (corpo e firma) — #218.
 * Riferimento di fedeltà: la finestra di composizione di Outlook
 * (Invio = riga singola, taglie in pt). Base di composizione: Arial 11pt (#230).
 */

export const EMAIL_SAFE_FONTS = [
  'Arial',
  'Calibri',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Verdana',
  'Tahoma',
] as const

/** Outlook/Word ragionano in pt: 11pt è il default di composizione. */
export const FONT_SIZES = [
  '8pt', '9pt', '10pt', '11pt', '12pt', '14pt', '18pt', '24pt', '36pt',
] as const

export const TEXT_COLORS = [
  { name: 'Nero', value: '#000000' },
  { name: 'Grigio scuro', value: '#4B5563' },
  { name: 'Grigio', value: '#9CA3AF' },
  { name: 'Rosso', value: '#DC2626' },
  { name: 'Arancione', value: '#EA580C' },
  { name: 'Giallo', value: '#CA8A04' },
  { name: 'Verde', value: '#16A34A' },
  { name: 'Blu', value: '#2563EB' },
  { name: 'Indaco', value: '#4F46E5' },
  { name: 'Viola', value: '#9333EA' },
] as const

export const EMAIL_DEFAULT_FONT_FAMILY = 'Arial, Helvetica, sans-serif'
/** Voce del dropdown corrispondente alla base: mostrata quando il testo non ha un mark esplicito. */
export const EMAIL_DEFAULT_FONT_NAME = 'Arial'

/** Il CSSOM quota i font multi-parola ("Times New Roman"): il Select vuole il nome nudo. */
export function fontFamilyDisplayName(fontFamily?: string | null): string {
  return (fontFamily ?? '').replace(/["']/g, '')
}
export const EMAIL_DEFAULT_FONT_SIZE = '11pt'
export const EMAIL_LINE_HEIGHT = 1.4

/** Stili del canvas: il testo si vede come nell'email inviata (wrapper del backend). */
export const EDITOR_CANVAS_STYLE: CSSProperties = {
  fontFamily: EMAIL_DEFAULT_FONT_FAMILY,
  fontSize: EMAIL_DEFAULT_FONT_SIZE,
  lineHeight: EMAIL_LINE_HEIGHT,
}

/** Azzera il margine dei <p> di ProseMirror: era duplicato nelle classNames di corpo e firma. */
export const EDITOR_CANVAS_PARAGRAPH_CLASS = '[&_.ProseMirror_p]:m-0'

// 'hr' è tenuto per i separatori di firma (email-safe, si vede in Outlook):
// estensione intenzionale della whitelist, il bottone toolbar della firma lo inserisce.
const ALLOWED_TAGS = new Set([
  'p', 'br', 'span', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'img', 'hr',
])

/** Rimappe Word/legacy → tag equivalenti della whitelist. */
const TAG_RENAMES: Record<string, string> = { b: 'strong', i: 'em' }

const IMG_ATTRS = new Set(['src', 'width', 'alt'])

/** Tag block-level: un div che ne contiene almeno uno va spacchettato, non rinominato.
 * hr incluso: un div con un hr dentro va spacchettato, non trasformato in <p> con hr dentro. */
const BLOCK_TAGS = new Set(['p', 'ul', 'ol', 'hr'])

/**
 * Cleaner DOM-based (sostituisce le regex di cleanHtmlPreservingFonts):
 * whitelist di tag e style, preserva gli attributi dei token.
 * Usato sia sulla serializzazione dell'editor sia sul paste.
 */
export function cleanEmailHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  cleanChildren(doc.body)
  return doc.body.innerHTML
}

function cleanChildren(parent: Element): void {
  let child = parent.firstChild
  while (child) {
    const next = child.nextSibling
    if (child.nodeType === Node.TEXT_NODE) {
      child = next
      continue
    }
    if (child.nodeType !== Node.ELEMENT_NODE) {
      parent.removeChild(child) // commenti, CDATA, ecc.
      child = next
      continue
    }

    let el = child as Element
    const tag = el.tagName.toLowerCase()

    if (tag === 'script' || tag === 'style') {
      el.remove()
      child = next
      continue
    }

    if (tag === 'div') {
      // Bottom-up: risolve prima i div/tag interni, poi decide come trattare questo div.
      cleanChildren(el)
      if (!hasBlockChild(el)) {
        const renamed = renameElement(el, 'p')
        scrubAttributes(renamed)
        child = renamed.nextSibling
        continue
      }
      unwrapDivWithBlockChild(parent, el)
      child = next
      continue
    }

    if (TAG_RENAMES[tag]) {
      el = renameElement(el, TAG_RENAMES[tag])
    }

    if (!ALLOWED_TAGS.has(el.tagName.toLowerCase())) {
      // Unwrap: i figli risalgono e vengono riprocessati a questo livello
      const first = el.firstChild
      while (el.firstChild) parent.insertBefore(el.firstChild, el)
      parent.removeChild(el)
      child = first ?? next
      continue
    }

    scrubAttributes(el)
    cleanChildren(el)
    child = el.nextSibling
  }
}

function hasBlockChild(el: Element): boolean {
  return Array.from(el.children).some((c) => BLOCK_TAGS.has(c.tagName.toLowerCase()))
}

/**
 * Spacchetta un div che contiene almeno un figlio block-level: i figli block
 * risalgono così come sono, i run di nodi inline consecutivi vengono avvolti
 * in un <p> (i run di soli spazi vengono scartati). I nodi risaliti sono già
 * stati puliti dalla ricorsione bottom-up in `cleanChildren`: il chiamante
 * riprende l'iterazione dal next sibling del div, senza riprocessarli.
 */
function unwrapDivWithBlockChild(parent: Element, div: Element): void {
  const doc = div.ownerDocument
  const nodes = Array.from(div.childNodes)
  let run: ChildNode[] = []

  const flushRun = () => {
    if (run.length === 0) return
    const isWhitespaceOnly = run.every(
      (n) => n.nodeType === Node.TEXT_NODE && !(n.textContent ?? '').trim(),
    )
    if (!isWhitespaceOnly) {
      const p = doc.createElement('p')
      for (const n of run) p.appendChild(n)
      parent.insertBefore(p, div)
    }
    run = []
  }

  for (const node of nodes) {
    const isBlock =
      node.nodeType === Node.ELEMENT_NODE &&
      BLOCK_TAGS.has((node as Element).tagName.toLowerCase())
    if (isBlock) {
      flushRun()
      parent.insertBefore(node, div)
    } else {
      run.push(node)
    }
  }
  flushRun()
  parent.removeChild(div)
}

function renameElement(el: Element, newTag: string): Element {
  const renamed = el.ownerDocument.createElement(newTag)
  while (el.firstChild) renamed.appendChild(el.firstChild)
  for (const attr of Array.from(el.attributes)) {
    renamed.setAttribute(attr.name, attr.value)
  }
  el.replaceWith(renamed)
  return renamed
}

function scrubAttributes(el: Element): void {
  const tag = el.tagName.toLowerCase()
  const isToken = el.hasAttribute('data-token-key')

  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase()
    if (name === 'style') continue // gestito sotto
    if (isToken && (name.startsWith('data-token-') || name === 'class')) continue
    if (tag === 'a' && name === 'href') continue
    if (tag === 'img' && IMG_ATTRS.has(name)) continue
    el.removeAttribute(attr.name)
  }

  const style = (el as HTMLElement).style
  const kept: string[] = []
  if (style.fontFamily) kept.push(`font-family: ${style.fontFamily}`)
  if (style.fontSize) kept.push(`font-size: ${style.fontSize}`)
  if (style.color) kept.push(`color: ${style.color}`)
  if (kept.length) {
    el.setAttribute('style', kept.join('; '))
  } else {
    el.removeAttribute('style')
  }
}

interface EmailEditorExtensionsOptions {
  placeholder: string
  /** Chip {{token}} — solo editor del corpo. */
  personalizationTokens?: boolean
  /** Upload immagini — solo editor firma. */
  images?: boolean
  /** Carica le immagini incollate/trascinate e ne restituisce l'URL (#250). */
  uploadImage?: ImageUploader
  onImageUploadError?: (error: unknown) => void
  /** Importa sul nostro storage le immagini http(s) esterne incollate (#262). */
  importImage?: ImageImporter
  /** src già sul nostro storage: né importato né rimosso al paste. */
  isStoredImage?: (src: string) => boolean
  /** Immagini incollate non recuperabili (file:, cid:, import fallito) rimosse. */
  onPastedImagesRemoved?: (count: number) => void
  /** Larghezza naturale delle immagini incollate, per la width di default (#263). */
  measureImageWidth?: ImageMeasurer
  /** Riceve upload e import di paste/drop in corso (#263). */
  trackImageWork?: (work: Promise<void>) => Promise<void>
  /** Liste/blocchi rich — corpo sì, firma no. */
  richBlocks?: boolean
}

/** Setup TipTap unico per corpo e firma: prima era duplicato e divergeva. */
export function buildEmailEditorExtensions(
  opts: EmailEditorExtensionsOptions,
): AnyExtension[] {
  return [
    // heading/blockquote/code* sono spenti ovunque: il cleaner li appiattisce al
    // salvataggio (non sono nella whitelist email-safe), quindi l'editor non deve
    // lasciarli comporre — nemmeno via shortcut markdown `# `, `> `, ``` (#227).
    StarterKit.configure({
      heading: false,
      blockquote: false,
      codeBlock: false,
      code: false,
      ...(opts.richBlocks
        ? {}
        : { bulletList: false, orderedList: false, listItem: false }),
    }),
    TextStyle,
    FontFamily.configure({ types: ['textStyle'] }),
    FontSize,
    Color.configure({ types: ['textStyle'] }),
    Link.configure({ openOnClick: false }),
    Placeholder.configure({ placeholder: opts.placeholder }),
    ...(opts.personalizationTokens ? [PersonalizationToken] : []),
    // allowBase64 resta false: niente data URI nel contenuto salvato (l'invio
    // allega le immagini come CID). Incolla/trascina passano da ImageUpload.
    ...(opts.images ? [Image.configure({ inline: false, allowBase64: false })] : []),
    ...(opts.images && opts.uploadImage
      ? [ImageUpload.configure({
          upload: opts.uploadImage,
          onError: opts.onImageUploadError ?? (() => {}),
          importRemote: opts.importImage ?? null,
          isStored: opts.isStoredImage ?? (() => false),
          onRemoved: opts.onPastedImagesRemoved ?? (() => {}),
          measureWidth: opts.measureImageWidth ?? null,
          track: opts.trackImageWork ?? ((work) => work),
        })]
      : []),
  ]
}

/** Il paste passa dal cleaner: incollare da Outlook/Word conserva font e colori. */
export const emailEditorProps: EditorOptions['editorProps'] = {
  transformPastedHTML: (html: string) => cleanEmailHtml(html),
}
